import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PiggyBank, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { v: "vat", l: "Umsatzsteuer" },
  { v: "income_tax", l: "Einkommensteuer" },
  { v: "emergency", l: "Notfall" },
  { v: "bonus", l: "Bonus / Entnahme" },
  { v: "custom", l: "Individuell" },
];
const BASES = [
  { v: "revenue_net", l: "% vom Netto-Umsatz (12 M)" },
  { v: "profit_pre_tax", l: "% vom Vor-Steuer-Gewinn (12 M)" },
  { v: "fixed_monthly", l: "Anzahl Monatsfixkosten" },
  { v: "none", l: "Nur fester Ziel-Betrag" },
];
const SEVERITY_STYLES = {
  red: "border-red-300 bg-red-50",
  yellow: "border-amber-200 bg-amber-50",
  green: "border-emerald-300 bg-emerald-50",
  neutral: "border-neutral-200 bg-white",
};

const empty = {
  name: "", kind: "custom", basis: "revenue_net", percent: 0,
  fixed_target_amount: "", current_amount: 0, account_id: "",
  color: "#22C55E", sort_order: 0, active: true, notes: "",
};

export default function ReservesPage() {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/reserve-buckets"), api.get("/accounts")]);
    setData(r.data); setAccounts(a.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      percent: Number(form.percent) || 0,
      current_amount: Number(form.current_amount) || 0,
      sort_order: Number(form.sort_order) || 0,
      fixed_target_amount: form.fixed_target_amount === "" || form.fixed_target_amount === null ? null : Number(form.fixed_target_amount),
      account_id: form.account_id || null,
    };
    try {
      if (editing) await api.patch(`/reserve-buckets/${editing.id}`, payload);
      else await api.post("/reserve-buckets", payload);
      toast.success(editing ? "Rücklagentopf aktualisiert" : "Rücklagentopf angelegt");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/reserve-buckets/${id}`);
    toast.success("Gelöscht"); load();
  };

  if (!data) return <div className="text-neutral-500" data-testid="reserves-loading">Lade…</div>;

  return (
    <div className="space-y-6" data-testid="reserves-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Cashflow-Steuerung</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-3">
            <PiggyBank className="h-7 w-7" /> Rücklagentöpfe
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Konfiguriere deine Rücklagen pro Topf (USt, Einkommensteuer, Notfall, Bonus …). Ziel-Betrag wird automatisch aus Umsatz, Gewinn oder Fixkosten berechnet.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="reserve-add-btn"><Plus className="h-4 w-4 mr-2" />Neuer Topf</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Rücklagentopf bearbeiten" : "Neuer Rücklagentopf"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reserve-name" /></div>
              <div>
                <Label>Typ</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="reserve-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map(k => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Basis für Ziel-Berechnung</Label>
                <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                  <SelectTrigger data-testid="reserve-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>{BASES.map(b => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.basis === "fixed_monthly" ? "Anzahl Monate" : "Prozent (%)"}</Label>
                <Input type="number" step="0.01" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} data-testid="reserve-percent" />
              </div>
              <div>
                <Label>Fester Ziel-Betrag (optional)</Label>
                <Input type="number" step="0.01" placeholder="überschreibt Berechnung" value={form.fixed_target_amount ?? ""} onChange={(e) => setForm({ ...form, fixed_target_amount: e.target.value })} />
              </div>
              <div>
                <Label>Aktueller Stand (€)</Label>
                <Input type="number" step="0.01" value={form.current_amount} onChange={(e) => setForm({ ...form, current_amount: e.target.value })} data-testid="reserve-current" />
              </div>
              <div>
                <Label>Verknüpftes Konto (optional)</Label>
                <Select value={form.account_id || "none"} onValueChange={(v) => setForm({ ...form, account_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Kein Konto —</SelectItem>
                    {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Farbe</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
              <div><Label>Sortierung</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notizen</Label><Input value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="reserve-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Summary label="Ziel-Summe gesamt" value={fmtCurrency(data.total_target)} testid="reserves-total-target" />
        <Summary label="Aktuell vorhanden" value={fmtCurrency(data.total_current)} testid="reserves-total-current" />
        <Summary label="Fehlbetrag" value={fmtCurrency(data.total_gap)} accent={data.total_gap > 0 ? "red" : "green"} testid="reserves-total-gap" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="reserves-list">
        {data.buckets.map((b, idx) => (
          <div key={b.id} className={`rounded-xl border-2 p-5 ${SEVERITY_STYLES[b.severity] || SEVERITY_STYLES.neutral}`} data-testid={`reserve-card-${idx}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: b.color }} />
                  <div className="font-display text-lg font-semibold truncate" data-testid={`reserve-name-${idx}`}>{b.name}</div>
                </div>
                {b.notes && <div className="text-xs text-neutral-600 mt-1">{b.notes}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(b); setForm({ ...empty, ...b, fixed_target_amount: b.fixed_target_amount ?? "", account_id: b.account_id || "" }); setOpen(true); }} data-testid={`reserve-edit-${idx}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(b.id)} data-testid={`reserve-delete-${idx}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>

            <div className="mt-4 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Ziel</span>
                <span className="font-semibold tabular-nums">{fmtCurrency(b.target_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Aktuell</span>
                <span className="font-semibold tabular-nums">{fmtCurrency(b.current_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Fehlbetrag</span>
                <span className={`font-semibold tabular-nums ${b.gap > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid={`reserve-gap-${idx}`}>{fmtCurrency(b.gap)}</span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-neutral-200 overflow-hidden">
                <div className="h-full" style={{
                  width: `${Math.min(100, b.coverage_pct ?? 0)}%`,
                  backgroundColor: b.color,
                }} />
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                {b.coverage_pct == null ? "Kein Ziel definiert" : `Deckungsgrad: ${b.coverage_pct.toFixed(1)} %`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Summary({ label, value, accent, testid }) {
  const color = accent === "red" ? "text-red-700" : accent === "green" ? "text-emerald-700" : "text-neutral-900";
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
